// The Projects TanStack layer. Projects owns its queries, its mutations and the
// updates to its own caches. The caches it does not own cross as reactions: the
// app's other lists that show projects (the chat sidebar's), and the billing
// caches a turn moves. The binding at `src/features/projects/hooks.ts` supplies
// them, and each runs where the pre-split hooks ran the same call.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import {
  isProjectQueryForbidden,
  projectsQueryKeys,
  type CreateProjectRequest,
  type ProjectListQuery,
  type SubmitProjectFeedbackRequest,
  type ProjectSubmitTurnRequest as SubmitTurnRequest,
  type UpdateProjectRequest,
} from '../../contracts';
import { projectsClient, type ProjectListPage } from '../api/projects';
import { injected, useIsAuthenticated } from '../injected-auth';
import { isCreditAllowanceExhausted } from './credit-allowance-exhausted';

/**
 * The app's reactions to a Projects write. Each hook takes the members it calls,
 * so a binding that drops one fails to compile rather than skipping a cache.
 */
export interface ProjectCacheReactions {
  /** Invalidate the app's other lists that show projects, right after the project lists. */
  invalidateOtherLists: (queryClient: QueryClient) => void;
  /**
   * Remove a deleted project from those lists before the request settles, and
   * return the rollback that restores what they held.
   */
  removeFromOtherLists: (queryClient: QueryClient, projectId: string) => () => void;
  /** A turn was refused because the credit allowance is exhausted. */
  onCreditAllowanceExhausted: () => void;
  /** A turn was accepted, after the detail and list invalidations. */
  onTurnSubmitted: () => void;
}

/** The Heidi Work verdict, resolved app-side (it ORs PostHog, which this package cannot read). */
const useIsHeidiWorkEnabled = (): boolean => injected('useIsHeidiWorkEnabled')();

export const invalidateProjectLists = (qc: QueryClient) => {
  // Any list query — sidebar (limit=6), landing page (limit=50), or
  // future paginated cursors — shares the ``['projects', 'list']``
  // prefix so a single invalidate flushes every shape at once.
  void qc.invalidateQueries({ queryKey: ['projects', 'list'] });
};

const invalidateLists = (
  qc: QueryClient,
  reactions: Pick<ProjectCacheReactions, 'invalidateOtherLists'>
) => {
  invalidateProjectLists(qc);
  reactions.invalidateOtherLists(qc);
};

/** Flag on and session ready — blocks Work queries before auth settles (pre-auth 401 → logout). */
const useAuthGatedEnabled = (flagEnabled: boolean): boolean => {
  const isAuthenticated = useIsAuthenticated();
  return flagEnabled && isAuthenticated;
};

/**
 * Unified projects list hook.
 *
 * Replaces the previous ``useProjectsList`` + ``useSavedProjects``
 * pair, which fired two requests and merged client-side. The backend
 * now sorts ``is_saved DESC, updated_at DESC`` for every query, so a
 * single ``GET /projects`` is enough to drive both the sidebar
 * (saved-first, capped) and the landing page (paginated, searchable).
 *
 * Pass ``saved: 'only'`` for the legacy "saved tab" view, ``'exclude'``
 * for a "recent unsaved" view, or omit for the default mixed list.
 */
export const useProjectsList = (
  query: ProjectListQuery = {},
  { enabled: enabledOverride = true }: { enabled?: boolean } = {}
) => {
  const enabled = useAuthGatedEnabled(useIsHeidiWorkEnabled()) && enabledOverride;
  return useQuery({
    queryKey: projectsQueryKeys.list(query),
    enabled,
    queryFn: async () => (await projectsClient.list(query)).data,
  });
};

export const useProject = (projectId: string | undefined) => {
  const enabled = useAuthGatedEnabled(useIsHeidiWorkEnabled());
  return useQuery({
    queryKey: projectsQueryKeys.detail(projectId ?? ''),
    enabled: enabled && !!projectId,
    queryFn: async () => (await projectsClient.get(projectId!)).data,
    // 403 is a revoked grant — do not retry it so the empty state can render.
    retry: (failureCount, error) => !isProjectQueryForbidden(error) && failureCount < 3,
  });
};

export const useCreateProject = (
  reactions: Pick<ProjectCacheReactions, 'invalidateOtherLists'>
) => {
  const qc = useQueryClient();
  return useMutation({
    // User-initiated create: always attempt the request. The default
    // networkMode pauses the mutation (isPending forever — no request, no
    // timeout, no error) whenever `navigator.onLine` misreports offline,
    // which macOS does under VPN / interface flaps; the composer then spins
    // endlessly. 'always' turns a dead network into a fast, surfaced failure.
    networkMode: 'always',
    mutationFn: async (body: CreateProjectRequest) =>
      (await projectsClient.create(body)).data,
    onSuccess: (data) => {
      // Seed the detail cache with the freshly-created project so the
      // detail page (``useProject``) mounts with data already present —
      // no cold-fetch ``HeidiLoader``/"Loading…" flash on the Work-home
      // hand-off, and ``agent_run_id`` is available immediately so the
      // seeded first turn fires without waiting on a round-trip. Create
      // and get return the same ``ProjectDetail`` shape.
      qc.setQueryData(projectsQueryKeys.detail(data.project_id), data);
      invalidateLists(qc, reactions);
      // work_project_created is BE-owned — no FE emit on create success.
    },
  });
};

export const useUpdateProject = (
  projectId: string,
  reactions: Pick<ProjectCacheReactions, 'invalidateOtherLists'>
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateProjectRequest) =>
      (await projectsClient.update(projectId, body)).data,
    onSuccess: (data) => {
      qc.setQueryData(projectsQueryKeys.detail(projectId), data);
      invalidateLists(qc, reactions);
    },
  });
};

interface DeleteProjectContext {
  /** Restores the app's other lists, which `removeFromOtherLists` snapshotted first. */
  rollbackOtherLists: () => void;
  previousProjectLists: [readonly unknown[], ProjectListPage | undefined][];
}

/**
 * Delete a project (and its harness-backed chat row) by ``project_id``.
 *
 * Takes the id at mutate time rather than hook init so a single instance
 * can drive bulk deletes from list surfaces (e.g. ``ChatSidebar``'s
 * selection-based delete dialog, which can include both legacy v1 chats
 * and v2 project chats in one confirmation).
 *
 * Optimistically removes the row from every cache it can appear in —
 * the projects list (Work landing page), saved chats, and both shapes
 * of the chats list (flat ``[GET_CHATS, page, pageSize]`` and infinite
 * ``[GET_CHATS, 'infinite', ...]`` used by the sidebar) — because the
 * backend surfaces v2 project chats through ``/v2/chat/chats`` via a
 * join, so the chats list refetch on ``settled`` would otherwise
 * re-materialise the deleted row in the sidebar until the project is
 * fully gone. The chat caches are the app's: they arrive as
 * ``removeFromOtherLists``, and are removed before the project lists.
 */
export const useDeleteProject = (
  reactions: Pick<ProjectCacheReactions, 'invalidateOtherLists' | 'removeFromOtherLists'>
) => {
  const qc = useQueryClient();
  return useMutation<void, Error, string, DeleteProjectContext>({
    mutationFn: async (projectId) => {
      await projectsClient.remove(projectId);
    },
    onMutate: (projectId) => {
      const rollbackOtherLists = reactions.removeFromOtherLists(qc, projectId);

      const previousProjectLists = qc.getQueriesData<ProjectListPage>({
        queryKey: ['projects', 'list'],
      });
      qc.setQueriesData<ProjectListPage>({ queryKey: ['projects', 'list'] }, (old) => {
        if (!old) return old;
        return { ...old, items: old.items.filter((p) => p.project_id !== projectId) };
      });

      return {
        rollbackOtherLists,
        previousProjectLists,
      };
    },
    onError: (_error, _projectId, context) => {
      if (!context) return;
      context.rollbackOtherLists();
      for (const [key, data] of context.previousProjectLists) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: (_data, _error, projectId) => {
      // Drop the project's detail cache so a re-navigation to a stale
      // bookmark fetches fresh (and 404s instead of resurrecting the
      // pre-delete snapshot).
      qc.removeQueries({ queryKey: projectsQueryKeys.detail(projectId) });
      invalidateLists(qc, reactions);
    },
  });
};

export const useToggleSavedProject = (
  projectId: string,
  reactions: Pick<ProjectCacheReactions, 'invalidateOtherLists'>
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (isSaved: boolean) =>
      (isSaved
        ? await projectsClient.save(projectId)
        : await projectsClient.unsave(projectId)
      ).data,
    onSuccess: (data) => {
      qc.setQueryData(projectsQueryKeys.detail(projectId), data);
      invalidateLists(qc, reactions);
    },
  });
};

export const useSubmitProjectTurn = (
  projectId: string,
  reactions: Pick<
    ProjectCacheReactions,
    'invalidateOtherLists' | 'onCreditAllowanceExhausted' | 'onTurnSubmitted'
  >
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SubmitTurnRequest) =>
      (await projectsClient.submitTurn(projectId, body)).data,
    onError: (error) => {
      if (isCreditAllowanceExhausted(error)) {
        reactions.onCreditAllowanceExhausted();
      }
    },
    onSuccess: () => {
      // Backend auto-titles a still-placeholder project from the first
      // prompt during `submit_turn`. Invalidate the detail + lists so
      // the new title lands in the header and in the chats sidebar
      // without waiting for the next route navigation.
      void qc.invalidateQueries({ queryKey: projectsQueryKeys.detail(projectId) });
      invalidateLists(qc, reactions);
      // A turn moves billing state the app caches (gamify progress, trial end).
      reactions.onTurnSubmitted();
    },
  });
};

/**
 * Submit a thumbs rating (+ optional comment) on an assistant reply.
 *
 * Pure side-effect mutation — feedback is write-only, so there's no
 * cache to update on success. The transcript query is left untouched
 * (a rating doesn't change the conversation), and the action bar owns
 * its own optimistic selected-state so the thumb highlights instantly.
 */
export const useSubmitProjectFeedback = (projectId: string) => {
  return useMutation({
    mutationFn: async (body: SubmitProjectFeedbackRequest) =>
      (await projectsClient.submitFeedback(projectId, body)).data,
  });
};

/**
 * Fetch LLM-generated follow-up questions for the project's latest
 * reply. Mirrors Evidence's ``FollowUpChips`` query semantics: fetched
 * lazily (only when ``enabled`` — e.g. the user opened the Follow-up
 * panel), keyed per reply, and cached indefinitely since the questions
 * are tied to that specific response.
 */
export const useProjectFollowUpQuestions = (
  projectId: string,
  {
    replyId,
    enabled,
    displayLanguage,
  }: { replyId: string; enabled: boolean; displayLanguage?: string }
) => {
  const authEnabled = useAuthGatedEnabled(enabled);
  return useQuery({
    queryKey: projectsQueryKeys.followUp(projectId, replyId, displayLanguage),
    enabled: authEnabled && !!projectId && !!replyId,
    queryFn: async () =>
      (
        await projectsClient.getFollowUpQuestions(
          projectId,
          displayLanguage ? { display_language: displayLanguage } : {}
        )
      ).data,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    retry: false,
  });
};
